

import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Modal, ModalDialog, ModalClose,
  Grid, Card, Avatar, Chip, IconButton
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import StyledTable from '../../components/StyledTable';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

const RoleManager = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ role_name: '', description: '' });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('user_roles').select('*').order('created_at', { ascending: false });
    if (!error) setRoles(data);
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (editingRole) {
      const { error } = await supabase.from('user_roles').update(form).eq('id', editingRole.id);
      if (!error) {
        setOpenModal(false);
        setEditingRole(null);
        setForm({ role_name: '', description: '' });
        fetchRoles();
      }
    } else {
      const { error } = await supabase.from('user_roles').insert([form]);
      if (!error) {
        setOpenModal(false);
        setForm({ role_name: '', description: '' });
        fetchRoles();
      }
    }
  };

  const handleDeleteRole = async (id) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      const { error } = await supabase.from('user_roles').delete().eq('id', id);
      if (!error) {
        fetchRoles();
      }
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setForm({ role_name: role.role_name, description: role.description });
    setOpenModal(true);
  };

  // Helper functions
  const getRoleInitials = (roleName) => {
    return roleName?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'RL';
  };

  const getRoleColor = (roleName) => {
    const colors = {
      'Admin': '#dc3545',
      'Manager': '#ffc107', 
      'User': '#28a745',
      'Viewer': '#6c757d',
      'Editor': '#17a2b8'
    };
    return colors[roleName] || '#00A884';
  };

  // Statistics
  const totalRoles = roles.length;
  const adminRoles = roles.filter(r => r.role_name?.toLowerCase().includes('admin')).length;
  const userRoles = roles.filter(r => r.role_name?.toLowerCase().includes('user')).length;
  const customRoles = roles.filter(r => !['Admin', 'Manager', 'User', 'Viewer', 'Editor'].includes(r.role_name)).length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Table Loading */}
        <CardSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Role Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            admin_panel_settings
          </span>
          Role Management Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Roles"
              value={totalRoles}
              icon="admin_panel_settings"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Admin Roles"
              value={adminRoles}
              icon="shield_person"
              gradientColors={['#dc3545', '#c82333']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="User Roles"
              value={userRoles}
              icon="group"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Custom Roles"
              value={customRoles}
              icon="tune"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add Role Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                security
              </span>
              Role Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create, edit, and manage user roles and permissions
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={() => { setForm({ role_name: '', description: '' }); setEditingRole(null); setOpenModal(true); }}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            Add Role
          </EnhancedButton>
        </Box>
      </Card>

      {/* Roles Table */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            badge
          </span>
          Role Directory ({totalRoles})
        </Typography>
        
        <StyledTable
          columns={[
            { key: 'role_info', label: 'Role', width: 250 },
            { key: 'description', label: 'Description', width: 400 },
            { key: 'permissions', label: 'Level', width: 150 },
            { key: 'actions', label: 'Actions', width: 120 }
          ]}
          rows={roles.map((role) => ({
            role_info: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: getRoleColor(role.role_name),
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem'
                  }}
                >
                  {getRoleInitials(role.role_name)}
                </Avatar>
                <Box>
                  <Typography level="title-sm" sx={{ color: 'success.700' }}>
                    {role.role_name || 'Unnamed Role'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Role ID: {role.id}
                  </Typography>
                </Box>
              </Box>
            ),
            description: (
              <Typography level="body-sm">
                {role.description || 'No description provided'}
              </Typography>
            ),
            permissions: (
              <Chip 
                size="sm" 
                variant="soft" 
                sx={{
                  bgcolor: `${getRoleColor(role.role_name)}20`,
                  color: getRoleColor(role.role_name)
                }}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {role.role_name?.toLowerCase().includes('admin') ? 'shield' : 
                     role.role_name?.toLowerCase().includes('manager') ? 'supervisor_account' :
                     role.role_name?.toLowerCase().includes('user') ? 'person' : 'visibility'}
                  </span>
                }
              >
                {role.role_name?.toLowerCase().includes('admin') ? 'High' : 
                 role.role_name?.toLowerCase().includes('manager') ? 'Medium' :
                 role.role_name?.toLowerCase().includes('user') ? 'Standard' : 'Limited'}
              </Chip>
            ),
            actions: (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="sm" 
                  variant="outlined" 
                  onClick={() => handleEdit(role)}
                  sx={{ '&:hover': { borderColor: 'success.500', color: 'success.600' }}}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    edit
                  </span>
                </IconButton>
                <IconButton 
                  size="sm" 
                  color="danger" 
                  variant="outlined" 
                  onClick={() => handleDeleteRole(role.id)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    delete
                  </span>
                </IconButton>
              </Box>
            )
          }))}
        />
      </Box>

      {/* Enhanced Role Modal */}
      <Modal open={openModal} onClose={() => { setOpenModal(false); setEditingRole(null); }}>
        <ModalDialog 
          sx={{ 
            maxWidth: '500px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editingRole ? 'edit' : 'security'}
            </span>
            {editingRole ? 'Edit Role' : 'Add New Role'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Role Name"
                value={form.role_name}
                onChange={e => setForm({ ...form, role_name: e.target.value })}
                placeholder="Project Manager"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    badge
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <EnhancedInput
                label="Description"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Role description and responsibilities..."
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    description
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => { setOpenModal(false); setEditingRole(null); }}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSubmit}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      {editingRole ? 'save' : 'add'}
                    </span>
                  }
                >
                  {editingRole ? 'Update Role' : 'Create Role'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default RoleManager;